﻿import { useDispatch } from 'react-redux';
import type { Dispatch, SetStateAction } from 'react';
import type {
  TApiSubmitApplicationResponse,
  TApiApplicationDocument,
} from '~/modules/covenants/types.ts';
import type { TLocalFileEntry } from '~/modules/covenants/types.ts';
import {
  hasSignatureValidationIssues,
  openOrDownloadBlob,
  resolveCovenantsRequestErrorMessage,
} from '~/modules/covenants/utils.ts';
import { errorMessages } from '~/constants/errorMessages.ts';
import {
  fetchApplication,
  fetchApplicationDocuments,
  recreateApplication,
  submitApplication,
} from '~/store/covenants/thunks/applicationThunks.ts';
import { downloadApplicationDocument } from '~/store/covenants/thunks/documentThunks.ts';
import { AppDispatch } from '~/store/store.ts';
import { TApplicationStatus } from '~/modules/covenants/types.ts';

export const useCovenantsApplicationActions = ({
  applicationId,
  contragentId,
  applicationStatus,
  isSubmitting,
  documentsPage,
  documentsRowsPerPage,
  setIsSubmitting,
  setIsSignatureModalOpen,
  onError,
  onValidationError,
  onSuccess,
  onRecreateSuccess,
}: {
  applicationId?: string;
  contragentId: number;
  applicationStatus?: TApplicationStatus;
  isSubmitting: boolean;
  documentsPage: number;
  documentsRowsPerPage: number;
  setIsSubmitting: Dispatch<SetStateAction<boolean>>;
  setIsSignatureModalOpen: Dispatch<SetStateAction<boolean>>;
  onError: (message: string) => void;
  onValidationError: (
    response: TApiSubmitApplicationResponse,
    refreshedDocuments?: TApiApplicationDocument[]
  ) => void | Promise<void>;
  onSuccess: () => void;
  onRecreateSuccess: (applicationId: string) => void;
}) => {
  const dispatch = useDispatch<AppDispatch>();

  const refreshApplicationData = async () => {
    if (!applicationId || !contragentId) {
      return null;
    }

    const app = await dispatch(fetchApplication({ contragentId, applicationId })).unwrap();

    const documentsResponse = await dispatch(
      fetchApplicationDocuments({
        contragentId,
        applicationId: app.id,
        page: documentsPage,
        size: documentsRowsPerPage,
        status: app.status,
      })
    ).unwrap();

    return {
      app,
      documents: documentsResponse.applicationDocuments,
    };
  };

  const handleSubmitCurrentApplication = async () => {
    if (!applicationId || !contragentId || isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    try {
      const latestApplication = await dispatch(
        fetchApplication({ contragentId, applicationId })
      ).unwrap();

      const submitResult = await dispatch(
        submitApplication({
          contragentId,
          applicationId,
          version: latestApplication.version,
        })
      ).unwrap();

      if (hasSignatureValidationIssues(submitResult)) {
        setIsSignatureModalOpen(false);
        let refreshedDocuments: TApiApplicationDocument[] | undefined;
        try {
          const refreshedApplicationData = await refreshApplicationData();
          refreshedDocuments = refreshedApplicationData?.documents;
        } catch (refreshError) {
          console.error('Не вдалось оновити дані:', refreshError);
        }

        await onValidationError(submitResult, refreshedDocuments);
        return;
      }

      if (!(submitResult.generatedProtocols || []).length) {
        onError(errorMessages.SERVICE_UNAVAILABLE);
        return;
      }

      setIsSignatureModalOpen(false);
      onSuccess();
    } catch (error) {
      onError(resolveCovenantsRequestErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRecreateFromDenied = async () => {
    if (!applicationId || !contragentId || !applicationStatus || isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    try {
      const recreatedApplication = await dispatch(
        recreateApplication({
          contragentId,
          applicationId,
        })
      ).unwrap();

      onRecreateSuccess(recreatedApplication.id);
    } catch (error) {
      onError(resolveCovenantsRequestErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePreviewDocument = async (doc: TApiApplicationDocument, localFile?: TLocalFileEntry) => {
    if (localFile?.file) {
      openOrDownloadBlob({
        blob: localFile.file,
        fileName: localFile.file.name || doc.fileName,
        fallbackFileName: doc.documentName || 'document',
        contentType: localFile.file.type,
      });
      return;
    }

    const fileId = doc.fileId ?? doc.id;

    if (!fileId) {
      console.warn('Missing parameters for document preview:', {
        fileId,
        documentRowId: doc.id,
        documentId: doc.documentId,
      });
      return;
    }

    try {
      const result = await dispatch(
        downloadApplicationDocument({
          fileId,
          isAttached: false,
        })
      ).unwrap();

      openOrDownloadBlob({
        blob: result.data,
        fileName: doc.fileName || result.filename,
        fallbackFileName: doc.documentName || 'document',
        contentType: result.contentType,
      });
    } catch (error) {
      console.error('Помилка завантаження документа:', error);
    }
  };

  const handleDownloadDocument = async (
    doc: TApiApplicationDocument,
    localFile?: TLocalFileEntry
  ) => {
    if (localFile?.file) {
      openOrDownloadBlob({
        blob: localFile.file,
        fileName: localFile.file.name || doc.fileName,
        fallbackFileName: doc.documentName || 'document',
        contentType: localFile.file.type,
        forceDownload: true,
      });
      return;
    }

    const fileId = doc.fileId ?? doc.id;

    if (!fileId) {
      console.warn('Missing parameters for document download:', {
        fileId,
        documentRowId: doc.id,
        documentId: doc.documentId,
      });
      return;
    }

    try {
      const result = await dispatch(
        downloadApplicationDocument({
          fileId,
          isAttached: false,
        })
      ).unwrap();

      openOrDownloadBlob({
        blob: result.data,
        fileName: doc.fileName || result.filename,
        fallbackFileName: doc.documentName || 'document',
        contentType: result.contentType,
        forceDownload: true,
      });
    } catch (error) {
      console.error('Помилка скачування документа:', error);
    }
  };

  return {
    handleSubmitCurrentApplication,
    handleRecreateFromDenied,
    handlePreviewDocument,
    handleDownloadDocument,
  };
};
